import { useEffect, useState } from "react";
import { BiKey, BiLockAlt } from "react-icons/bi";
import { useToast } from "../../../components/Toast/ToastContext";
import {
  checkPasscodeApi,
  setFinanceLockEnabledApi,
  setPasscodeApi,
} from "../../../services/passcodeApi";
import { apiErrorMessage, changePassword } from "../../../services/settingsApi";
import {
  Card,
  Field,
  Modal,
  Toggle,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "../settingsUi";

const PasscodeModal = ({
  open,
  hasPasscode,
  onClose,
  onSaved,
}: {
  open: boolean;
  hasPasscode: boolean;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const { notify } = useToast();
  const [password, setPassword] = useState("");
  const [passcode, setPasscode] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPassword("");
    setPasscode("");
    setConfirm("");
    setError(null);
  }, [open]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{4,6}$/.test(passcode)) return setError("Use 4 to 6 digits.");
    if (passcode !== confirm) return setError("The passcodes don't match.");
    setSaving(true);
    setError(null);
    try {
      await setPasscodeApi(password, passcode);
      notify("Passcode saved.", { type: "success" });
      onSaved();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't save the passcode."));
    } finally {
      setSaving(false);
    }
  };

  const digitsOnly = (value: string) => value.replace(/\D/g, "").slice(0, 6);

  return (
    <Modal
      open={open}
      title={hasPasscode ? "Change Finance passcode" : "Set Finance passcode"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={secondaryButtonClass} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="passcode-form" className={primaryButtonClass} disabled={saving}>
            {saving ? "Saving…" : "Save passcode"}
          </button>
        </>
      }
    >
      <form id="passcode-form" onSubmit={save} className="space-y-4">
        <p className="text-sm text-slate-500">
          The passcode locks your Finance screen on the website and in the app.
        </p>
        <Field label="Account password">
          <input
            className={inputClass}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>
        <Field label="New passcode" hint="4 to 6 digits">
          <input
            className={inputClass}
            type="password"
            inputMode="numeric"
            value={passcode}
            onChange={(e) => setPasscode(digitsOnly(e.target.value))}
            autoComplete="off"
            required
          />
        </Field>
        <Field label="Confirm passcode">
          <input
            className={inputClass}
            type="password"
            inputMode="numeric"
            value={confirm}
            onChange={(e) => setConfirm(digitsOnly(e.target.value))}
            autoComplete="off"
            required
          />
        </Field>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </form>
    </Modal>
  );
};

const PasswordModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { notify } = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCurrent("");
    setNext("");
    setConfirm("");
    setError(null);
  }, [open]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next.length < 6) return setError("Use at least 6 characters.");
    if (next !== confirm) return setError("The new passwords don't match.");
    if (next === current) return setError("Pick a password different from your current one.");
    setSaving(true);
    setError(null);
    try {
      await changePassword(current, next);
      notify("Password changed.", { type: "success" });
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't change your password."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Change password"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={secondaryButtonClass} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="password-form" className={primaryButtonClass} disabled={saving}>
            {saving ? "Saving…" : "Change password"}
          </button>
        </>
      }
    >
      <form id="password-form" onSubmit={save} className="space-y-4">
        <Field label="Current password">
          <input
            className={inputClass}
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>
        <Field label="New password" hint="At least 6 characters">
          <input
            className={inputClass}
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>
        <Field label="Confirm new password">
          <input
            className={inputClass}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </form>
    </Modal>
  );
};

const SecuritySection = () => {
  const { notify } = useToast();
  const [passcode, setPasscode] = useState<{ exists: boolean; enabled: boolean } | null>(null);
  const [passcodeOpen, setPasscodeOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [savingLock, setSavingLock] = useState(false);

  const loadPasscode = () =>
    checkPasscodeApi()
      .then(setPasscode)
      .catch(() => setPasscode({ exists: false, enabled: false }));

  useEffect(() => {
    loadPasscode();
  }, []);

  const toggleLock = async (enabled: boolean) => {
    setSavingLock(true);
    try {
      const result = await setFinanceLockEnabledApi(enabled);
      setPasscode((p) => (p ? { ...p, enabled: result.enabled } : p));
      notify(enabled ? "Finance is now locked with your passcode." : "Finance lock turned off.", {
        type: "success",
      });
    } catch (error) {
      notify(apiErrorMessage(error, "Couldn't change the Finance lock."), { type: "error" });
    } finally {
      setSavingLock(false);
    }
  };

  return (
    <div className="space-y-5">
      <Card
        icon={BiLockAlt}
        title="Finance passcode"
        description="A 4–6 digit code that keeps your Finance numbers private on shared devices."
        action={
          <button
            type="button"
            className={secondaryButtonClass}
            onClick={() => setPasscodeOpen(true)}
            disabled={!passcode}
          >
            {passcode?.exists ? "Change passcode" : "Set passcode"}
          </button>
        }
      >
        {passcode?.exists ? (
          <Toggle
            label="Lock Finance with passcode"
            description="Ask for the passcode before showing Finance."
            checked={passcode.enabled}
            onChange={toggleLock}
            disabled={savingLock}
          />
        ) : (
          passcode && <p className="text-sm text-slate-500">No passcode set yet.</p>
        )}
      </Card>

      <Card
        icon={BiKey}
        title="Password"
        description="The password you sign in with on the website and in the app."
        action={
          <button type="button" className={secondaryButtonClass} onClick={() => setPasswordOpen(true)}>
            Change password
          </button>
        }
      />

      <PasscodeModal
        open={passcodeOpen}
        hasPasscode={!!passcode?.exists}
        onClose={() => setPasscodeOpen(false)}
        onSaved={loadPasscode}
      />
      <PasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </div>
  );
};

export default SecuritySection;
